import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Button, Table, Alert, Modal, Form, Row, Col, Collapse } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { CenteredLoader } from '../../../components/LoadingSpinner';
import { api } from '../../../shared/api';
import Select from '../../../components/form/AppSelect';
import CreatableSelect from '../../../components/form/AppCreatableSelect';
import '../../../shared/ReactSelectDarkMode.css';
import { useTranslation } from 'react-i18next';
import { useDateFormat } from '../../../shared/DateFormatContext';
import TemplateEditor from './TemplateEditor';
import { usePermissions } from '../../../shared/permissions';
import { useConfirm } from '../../../shared/ConfirmContext';
import { useRealtimeEvents } from '../../../shared/useRealtimeEvents';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import EmptyState from '../../../components/feedback/EmptyState';
import PageSection from '../../../components/layout/PageSection';
import StatusBadge from '../../../components/ui/StatusBadge';

// Custom theme for React Select to support dark mode
const selectTheme = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: 'var(--tblr-primary)',
    primary75: 'var(--tblr-primary-rgb)',
    primary50: 'var(--tblr-primary-rgb)',
    primary25: 'var(--tblr-primary-rgb)',
    danger: 'var(--tblr-danger)',
    dangerLight: 'var(--tblr-danger-rgb)',
    neutral0: 'var(--tblr-bg-forms)',
    neutral5: 'var(--tblr-bg-surface)',
    neutral10: 'var(--tblr-bg-surface)',
    neutral20: 'var(--tblr-border-color)',
    neutral30: 'var(--tblr-border-color)',
    neutral40: 'var(--tblr-body-color-secondary)',
    neutral50: 'var(--tblr-body-color-secondary)',
    neutral60: 'var(--tblr-body-color-secondary)',
    neutral70: 'var(--tblr-body-color-secondary)',
    neutral80: 'var(--tblr-body-color)',
    neutral90: 'var(--tblr-body-color)'
  }
});

// Debounce function to limit API calls during typing
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const VERSION_BADGE_COLOR = '#6c7a91';

const ProjectOffers = ({ projectId, projectDiscount = 0 }) => {
  const { t } = useTranslation(); // Add translation hook
  const { formatDate } = useDateFormat();
  const confirm = useConfirm();
  const { canManage } = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Search-overrideable select options (search handlers call setCustomerOptions/setContractorOptions)
  const [customerOptions, setCustomerOptions] = useState([]);
  const [contractorOptions, setContractorOptions] = useState([]);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [selectedContractorOption, setSelectedContractorOption] = useState(null);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isSavingOffer, setIsSavingOffer] = useState(false);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [editingOfferId, setEditingOfferId] = useState(null);
  const [expandedOfferId, setExpandedOfferId] = useState(null);
  const [isSnapshotSummaryOpen, setIsSnapshotSummaryOpen] = useState(false);
  const [viewingSnapshotOffer, setViewingSnapshotOffer] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    name: t('projects.offers.defaultName'),
    project: projectId,
    customer: '',
    contractor: '',
    date_issued: new Date().toISOString().split('T')[0],
    offer_valid_until: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
    version: '1.0',
    discount: projectDiscount,
    note: '',
    include_items: true,
    hide_prices: false,
    hide_unit_prices: true,
    include_customer_info: true,
    include_vat: true,
    show_category_totals: true,
    group_by_parent_category: true,
    hide_licence_plates: true,
    include_notes: true,
    hide_staff_info: true,
    template: 'default',
    template_content: '',
    description: ''
  });

  const _optionsInitialized = useRef(false);

  const { isLoading: loading, data: queryData } = useQuery({
    queryKey: ['project-offers-all', projectId],
    queryFn: async () => {
      const [offersRes, customersRes, contractorsRes, projectDataRes, costSummaryRes, equipmentRes, extItemsRes, staffRes, vehiclesRes] = await Promise.all([
        api.get(`projects/api/project-offers/?project_id=${projectId}`),
        api.get('customers/api/customers/'),
        api.get('crew/api/crew/'),
        api.get(`projects/api/projects/${projectId}/`),
        api.get(`projects/api/projects/${projectId}/cost-summary/`),
        api.get(`projects/api/project-equipment/?project=${projectId}`),
        api.get(`projects/api/project-external-items/?project=${projectId}`),
        api.get(`projects/api/project-staff/?project=${projectId}`),
        api.get(`projects/api/project-vehicles/?project=${projectId}`),
      ]);
      return {
        offers: offersRes,
        customers: customersRes,
        contractors: contractorsRes,
        projectData: projectDataRes,
        costSummary: costSummaryRes,
        projectItems: (Array.isArray(equipmentRes) ? equipmentRes : (equipmentRes.results || [])).map(equipment => ({
          id: equipment.id,
          item_name: equipment.product_name,
          quantity: equipment.quantity_requested,
        })),
        projectExternalItems: Array.isArray(extItemsRes) ? extItemsRes : (extItemsRes.results || []),
        projectStaff: Array.isArray(staffRes) ? staffRes : (staffRes.results || []),
        projectVehicles: Array.isArray(vehiclesRes) ? vehiclesRes : (vehiclesRes.results || []),
      };
    },
  });

  // Derive read-only display data directly from query result
  const offers = queryData?.offers ?? [];
  const customers = queryData?.customers ?? [];
  const contractors = queryData?.contractors ?? [];
  const projectData = queryData?.projectData ?? null;
  const costSummary = queryData?.costSummary ?? null;
  const projectItems = queryData?.projectItems ?? [];
  const projectExternalItems = queryData?.projectExternalItems ?? [];
  const projectStaff = queryData?.projectStaff ?? [];
  const projectVehicles = queryData?.projectVehicles ?? [];

  // Distinct offer names across ALL projects, most recent first, for the name picker
  const { data: offerNameHistory = [] } = useQuery({
    queryKey: ['project-offer-names'],
    queryFn: () => api.get('projects/api/project-offers/names/'),
    staleTime: 5 * 60 * 1000,
  });
  const offerNameOptions = useMemo(
    () => offerNameHistory.map((name) => ({ value: name, label: name })),
    [offerNameHistory]
  );

  // Summary panel data — switches between live project data and a snapshotted offer version
  const _snap = viewingSnapshotOffer?.snapshot;
  const summaryItems = _snap
    ? (_snap.items ?? []).map((i, idx) => ({ id: idx, name: i.name, quantity: `x${i.quantity}` }))
    : projectItems.map(pi => ({ id: pi.id, name: pi.item_name, quantity: `x${pi.quantity}` }));
  const summaryExternalItems = _snap
    ? (_snap.external_items ?? []).map((i, idx) => ({ id: `ext-${idx}`, name: i.name, quantity: `x${i.quantity}` }))
    : projectExternalItems.map(ext => ({ id: `ext-${ext.id}`, name: ext.name, quantity: `x${ext.quantity}` }));
  const summaryStaff = _snap
    ? (_snap.staff ?? []).map((m, idx) => ({ id: idx, name: m.name, role: m.role }))
    : projectStaff.map(ps => ({ id: ps.id, name: ps.crew_member_detail?.display_name || ps.display_name, role: ps.role }));
  const summaryVehicles = _snap
    ? (_snap.vehicles ?? []).map((v, idx) => ({ id: idx, name: v.name, tariff: v.rate_type === 'km' ? `${v.km} km` : `${v.km} dní` }))
    : projectVehicles.map(pv => ({ id: pv.id, name: pv.vehicle_name, tariff: pv.rate_type === 'km' ? `${pv.km} km` : `${pv.km} dní` }));

  // Initialize search-overrideable options once when query data first loads
  useEffect(() => {
    if (!queryData || _optionsInitialized.current) return;
    _optionsInitialized.current = true;
    setCustomerOptions(customers.map(c => ({ value: c.id, label: `${c.company_name || ''} | ${c.resp_person || ''}` })));
    setContractorOptions(contractors.map(c => ({ value: c.id, label: `${c.company?.company_name || ''} | ${c.display_name || ''}` })));
  }, [queryData]);

  const fetchOffers = useCallback((silent = false) => {
    queryClient.invalidateQueries({ queryKey: ['project-offers-all', projectId] });
  }, [queryClient, projectId]);

  const _offersDebounce = useRef(null);
  useRealtimeEvents((e) => {
    if (e.model !== 'projects.projectoffer') return;
    clearTimeout(_offersDebounce.current);
    _offersDebounce.current = setTimeout(() => fetchOffers(true), 400);
  });

  // Pre-seed customer option from project data so it's always ready
  useEffect(() => {
    if (projectData?.customer?.id) {
      const cd = projectData.customer;
      const opt = {
        value: cd.id,
        label: `${cd.company_name || ''} | ${cd.resp_person || ''}`
      };
      setSelectedCustomerOption(opt);
      setCustomerOptions(prev =>
        prev.some(o => o.value === cd.id) ? prev : [opt, ...prev]
      );
    }
    // Pre-fill contractor from project's processed_by
    if (projectData?.processed_by?.id) {
      const pb = projectData.processed_by;
      const contrOpt = {
        value: pb.id,
        label: `${pb.company?.company_name || ''} | ${pb.display_name || ''}`
      };
      setSelectedContractorOption(contrOpt);
      setFormData(prev => ({ ...prev, contractor: pb.id }));
      setContractorOptions(prev =>
        prev.some(o => o.value === pb.id) ? prev : [contrOpt, ...prev]
      );
    }
  }, [projectData]);

  const validateForm = ({ requireCustomer = false, requireContractor = false } = {}) => {
    const errors = {};
    if (!formData.name.trim()) errors.name = t('projects.offers.validation.nameRequired');
    if (requireCustomer && !formData.customer) errors.customer = t('projects.offers.validation.customerRequired');
    if (requireContractor && !formData.contractor) errors.contractor = t('projects.offers.validation.contractorRequired');
    if (!formData.date_issued) errors.date_issued = t('projects.offers.validation.dateIssuedRequired');
    if (!formData.offer_valid_until) errors.offer_valid_until = t('projects.offers.validation.validUntilRequired');
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };



  const fetchCustomers = async (searchTerm = '') => {
    try {
      let url = 'customers/api/customers/';
      if (searchTerm) {
        url += `?search=${encodeURIComponent(searchTerm)}`;
      }
      const response = await api.get(url);
      setCustomers(response);

      // Prepare options for React Select
      const options = response.map(customer => ({
        value: customer.id,
        label: `${customer.company_name || ''} | ${customer.resp_person || ''}`
      }));
      setCustomerOptions(options);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchContractors = async (searchTerm = '') => {
    try {
      let url = 'crew/api/crew/';
      if (searchTerm) {
        url += `?search=${encodeURIComponent(searchTerm)}`;
      }
      const response = await api.get(url);
      setContractors(response);

      // Prepare options for React Select
      const options = response.map(contractor => ({
        value: contractor.id,
        label: `${contractor.company?.company_name || ''} | ${contractor.display_name || ''}`
      }));
      setContractorOptions(options);
    } catch (err) {
      console.error('Error fetching contractors:', err);
    }
  };

  // Debounced search functions
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCustomerSearch = useCallback(
    debounce((inputValue) => {
      fetchCustomers(inputValue);
    }, 300),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedContractorSearch = useCallback(
    debounce((inputValue) => {
      fetchContractors(inputValue);
    }, 300),
    []
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleSelectChange = (name, selectedOption) => {
    if (name === 'customer') setSelectedCustomerOption(selectedOption);
    if (name === 'contractor') setSelectedContractorOption(selectedOption);
    setFormData({
      ...formData,
      [name]: selectedOption ? selectedOption.value : ''
    });
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: null }));
  };

  const handlePreview = async () => {
    if (!validateForm({ requireCustomer: false, requireContractor: false })) return;
    try {
      const { token } = await api.post('projects/api/project-offers/generate_preview_token/', formData);
      const baseUrl = api.getBaseUrl();
      window.open(`${baseUrl}/projects/api/project-offers/preview_pdf_token/?token=${token}`, '_blank');
    } catch (err) {
      console.error('Error generating preview:', err);
      toast.error(t('projects.offers.errorPreview'));
    }
  };

  const [previewLoading, setPreviewLoading] = useState(false);

  const handleQuickPreview = async () => {
    const customerId = projectData?.customer?.id;

    const payload = {
      project: projectId,
      customer: customerId || null,
      // Use the project's own "processed_by" if set — otherwise leave empty rather
      // than falling back to an arbitrary first contractor from the list.
      contractor: projectData?.processed_by?.id || null,
      name: t('projects.offers.defaultName'),
      date_issued: new Date().toISOString().split('T')[0],
      offer_valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      version: '1.0',
      discount: projectDiscount,
      note: '',
      include_items: true,
      hide_prices: false,
      hide_unit_prices: true,
      include_customer_info: true,
      include_vat: true,
      show_category_totals: true,
      group_by_parent_category: true,
      hide_licence_plates: true,
      include_notes: true,
      hide_staff_info: true,
      template: 'default',
      template_content: '',
      description: ''
    };

    // Open window synchronously (user-gesture context) so mobile browsers don't block it
    const newWindow = window.open('', '_blank');
    try {
      setPreviewLoading(true);
      const { token } = await api.post('projects/api/project-offers/generate_preview_token/', payload);
      const baseUrl = api.getBaseUrl();
      newWindow.location.href = `${baseUrl}/projects/api/project-offers/preview_pdf_token/?token=${token}`;
    } catch (err) {
      newWindow.close();
      console.error('Error generating quick preview:', err);
      toast.error(t('projects.offers.errorPreview'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDelete = async (offerId) => {
    if (await confirm(t('projects.offers.confirmDelete'))) {
      try {
        await api.delete(`projects/api/project-offers/${offerId}/`);
        fetchOffers();
      } catch (err) {
        console.error('Error deleting offer:', err);
        toast.error(t('projects.offers.errorDeleting'));
      }
    }
  };

  const handleEdit = async (offer) => {
    const custOpt = offer.customer_detail
      ? { value: offer.customer_detail.id, label: `${offer.customer_detail.company_name || ''} | ${offer.customer_detail.resp_person || ''}` }
      : null;
    const contrOpt = offer.contractor_detail
      ? { value: offer.contractor_detail.id, label: `${offer.contractor_detail.company?.company_name || ''} | ${offer.contractor_detail.display_name || ''}` }
      : null;
    setSelectedCustomerOption(custOpt);
    setSelectedContractorOption(contrOpt);
    // Populate the form with offer data
    setFormData({
      id: offer.id, // Add ID for update operation
      name: offer.name,
      project: offer.project,
      customer: offer.customer_detail?.id || '',
      contractor: offer.contractor_detail?.id || '',
      date_issued: new Date(offer.date_issued).toISOString().split('T')[0],
      offer_valid_until: new Date(offer.offer_valid_until).toISOString().split('T')[0],
      version: offer.version || '1.0',
      discount: offer.discount || 0,
      note: offer.note || '',
      include_items: offer.include_items !== false,
      hide_prices: offer.hide_prices || false,
      hide_unit_prices: offer.hide_unit_prices || false,
      include_customer_info: offer.include_customer_info !== false,
      include_vat: offer.include_vat !== false,
      show_category_totals: offer.show_category_totals !== false,
      group_by_parent_category: offer.group_by_parent_category !== false,
      hide_licence_plates: offer.hide_licence_plates || false,
      include_notes: offer.include_notes || false,
      hide_staff_info: offer.hide_staff_info !== false,
      template: offer.template || 'default',
      template_content: offer.template_content || '',
      description: offer.description || ''
    });
    setShowModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (isSavingOffer) return;
    if (!validateForm()) return;
    const offerPayload = {
      ...formData,
      customer: formData.customer || null,
      contractor: formData.contractor || null
    };
    setIsSavingOffer(true);
    try {
      // If formData has an id, it's an update operation
      if (formData.id) {
        await api.put(`projects/api/project-offers/${formData.id}/`, offerPayload);
        setShowModal(false);
        // Reset form
        setFormData({
          name: t('projects.offers.defaultName'),
          project: projectId,
          customer: '',
          contractor: '',
          date_issued: new Date().toISOString().split('T')[0],
          offer_valid_until: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
          version: '1.0',
          discount: projectDiscount,
          note: '',
          include_items: true,
          hide_prices: false,
          hide_unit_prices: true,
          include_customer_info: true,
          include_vat: true,
          show_category_totals: true,
          group_by_parent_category: true,
          hide_licence_plates: true,
          include_notes: true,
          hide_staff_info: true,
          template: 'default',
          template_content: '',
          description: ''
        });
        fetchOffers(); // Refresh data
      } else {
        // This is a create operation
        await api.post('projects/api/project-offers/', offerPayload);
        setShowModal(false);
        // Reset form
        setFormData({
          name: t('projects.offers.defaultName'),
          project: projectId,
          customer: '',
          contractor: '',
          date_issued: new Date().toISOString().split('T')[0],
          offer_valid_until: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
          version: '1.0',
          discount: projectDiscount,
          note: '',
          include_items: true,
          hide_prices: false,
          hide_unit_prices: true,
          include_customer_info: true,
          include_vat: true,
          show_category_totals: true,
          group_by_parent_category: true,
          hide_licence_plates: true,
          include_notes: true,
          hide_staff_info: true,
          template: 'default',
          template_content: '',
          description: ''
        });
        fetchOffers(); // Refresh data
      }
    } catch (err) {
      console.error('Error saving offer:', err);
      toast.error(t(formData.id ? 'projects.offers.errorUpdating' : 'projects.offers.errorCreating'));
    } finally {
      setIsSavingOffer(false);
    }
  };

  const triggerBlobDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleDownload = async (offerId) => {
    try {
      const { blob, filename } = await api.get(
        `projects/detail/${projectId}/offers/download/${offerId}`,
        { responseType: 'blob', withFilename: true }
      );
      triggerBlobDownload(blob, filename || `nabidka_${offerId}.pdf`);
    } catch (err) {
      console.error('PDF download failed:', err);
      toast.error(t('projects.offers.errorDownload', 'Stažení PDF se nezdařilo.'));
    }
  };

  const handleExportExcel = async (offerId) => {
    try {
      const { blob, filename } = await api.get(
        `projects/detail/${projectId}/offers/excel/${offerId}`,
        { responseType: 'blob', withFilename: true }
      );
      triggerBlobDownload(blob, filename || `nabidka_${offerId}.xlsx`);
    } catch (err) {
      console.error('Excel download failed:', err);
      toast.error(t('projects.offers.errorDownload', 'Stažení Excel se nezdařilo.'));
    }
  };

  const handleShow = async (offerId) => {
    try {
      const blob = await api.get(
        `projects/detail/${projectId}/offers/view/${offerId}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Revoke after a short delay to allow the tab to load
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error('PDF view failed:', err);
      toast.error(t('projects.offers.errorDownload', 'Otevření PDF se nezdařilo.'));
    }
  };

  const handleDuplicate = async (offer) => {
    const newVersion = (parseFloat(offer.version || '1') + 1).toFixed(1);
    try {
      await api.post('projects/api/project-offers/', {
        name: offer.name,
        project: offer.project,
        customer: offer.customer_detail?.id || '',
        contractor: offer.contractor_detail?.id || '',
        date_issued: new Date().toISOString().split('T')[0],
        offer_valid_until: offer.offer_valid_until,
        version: newVersion,
        discount: offer.discount || 0,
        note: offer.note || '',
        include_items: offer.include_items !== false,
        hide_prices: offer.hide_prices || false,
        hide_unit_prices: offer.hide_unit_prices || false,
        include_customer_info: offer.include_customer_info !== false,
        include_vat: offer.include_vat !== false,
        show_category_totals: offer.show_category_totals !== false,
        group_by_parent_category: offer.group_by_parent_category !== false,
        hide_licence_plates: offer.hide_licence_plates || false,
        include_notes: offer.include_notes || false,
        template: offer.template || 'default',
        template_content: offer.template_content || '',
        description: offer.description || ''
      });
      fetchOffers();
    } catch (err) {
      console.error('Error duplicating offer:', err);
      toast.error(t('projects.offers.errorDuplicating'));
    }
  };

  const handleRefreshSnapshot = async (offerId) => {
    if (!await confirm(t('projects.offers.confirmRefreshSnapshot'))) return;
    try {
      await api.post(`projects/api/project-offers/${offerId}/refresh_snapshot/`);
      await fetchOffers();
      toast.success(t('projects.offers.snapshotRefreshed'));
    } catch (err) {
      console.error('Error refreshing snapshot:', err);
      toast.error(t('projects.offers.errorRefreshSnapshot'));
    }
  };

  const handleRestoreSnapshot = async (offer) => {
    const snap = offer.snapshot;

    if (!snap) {
      toast.error(t('projects.offers.errorRestoreSnapshot'));
      return;
    }

    // Analyse snapshot before asking for confirmation
    const snapItems      = snap.items          || [];
    const snapVehicles   = snap.vehicles        || [];
    const snapStaff      = snap.staff           || [];
    const snapExtItems   = snap.external_items  || [];
    const snapGroups     = snap.staff_groups    || [];

    const skippedItems    = snapItems.filter(i => !i.item_id).length;
    const skippedVehicles = snapVehicles.filter(v => !v.vehicle_id).length;
    const skippedStaff    = snapStaff.filter(s => !s.crew_member_id).length;

    const allItemsSkipped    = snapItems.length    > 0 && skippedItems    === snapItems.length;
    const allVehiclesSkipped = snapVehicles.length > 0 && skippedVehicles === snapVehicles.length;
    const allStaffSkipped    = snapStaff.length    > 0 && skippedStaff    === snapStaff.length;
    const hasHighRisk        = allItemsSkipped || allVehiclesSkipped || allStaffSkipped;

    // Sections where current data would be fully wiped with nothing restored
    const itemsWipeRisk    = projectItems.length        > 0 && snapItems.length === 0;
    const vehiclesWipeRisk = projectVehicles.length     > 0 && snapVehicles.length === 0;
    const staffWipeRisk    = projectStaff.length        > 0 && (snapStaff.length + snapGroups.length) === 0;
    const extWipeRisk      = projectExternalItems.length > 0 && snapExtItems.length === 0;

    const confirmMessage = (
      <div>
        <p className="mb-2"
          dangerouslySetInnerHTML={{ __html: t('projects.offers.confirmRestoreSnapshotDesc', { name: offer.name, version: offer.version }) }}
        />

        {(hasHighRisk || itemsWipeRisk || vehiclesWipeRisk || staffWipeRisk) && (
          <div className="alert alert-danger py-2 px-3 mb-3" role="alert" style={{ fontSize: '0.875em' }}>
            {t('projects.offers.confirmRestoreSnapshotRisk')}
          </div>
        )}

        <table className="table table-sm table-bordered mb-3" style={{ fontSize: '0.825em' }}>
          <thead className="table-light">
            <tr>
              <th>{t('projects.offers.confirmRestoreSnapshotColSection')}</th>
              <th className="text-center">{t('projects.offers.confirmRestoreSnapshotColCurrent')}</th>
              <th className="text-center">{t('projects.offers.confirmRestoreSnapshotColSnapshot')}</th>
              <th className="text-center">{t('projects.offers.confirmRestoreSnapshotColSkipped')}</th>
            </tr>
          </thead>
          <tbody>
            <tr className={(allItemsSkipped || itemsWipeRisk) ? 'table-danger' : ''}>
              <td>{t('projects.offers.offerItems')}</td>
              <td className="text-center">{projectItems.length}</td>
              <td className="text-center">{snapItems.length}</td>
              <td className="text-center">{skippedItems > 0 ? <strong className="text-danger">{skippedItems}</strong> : 0}</td>
            </tr>
            <tr className={(allVehiclesSkipped || vehiclesWipeRisk) ? 'table-danger' : ''}>
              <td>{t('projects.offers.offerVehicles')}</td>
              <td className="text-center">{projectVehicles.length}</td>
              <td className="text-center">{snapVehicles.length}</td>
              <td className="text-center">{skippedVehicles > 0 ? <strong className="text-danger">{skippedVehicles}</strong> : 0}</td>
            </tr>
            <tr className={(allStaffSkipped || staffWipeRisk) ? 'table-danger' : ''}>
              <td>{t('projects.offers.offerStaff')}</td>
              <td className="text-center">{projectStaff.length}</td>
              <td className="text-center">{snapStaff.length + snapGroups.length}</td>
              <td className="text-center">{skippedStaff > 0 ? <strong className="text-danger">{skippedStaff}</strong> : 0}</td>
            </tr>
            <tr className={extWipeRisk ? 'table-danger' : ''}>
              <td>{t('projects.offers.confirmRestoreSnapshotExtItems')}</td>
              <td className="text-center">{projectExternalItems.length}</td>
              <td className="text-center">{snapExtItems.length}</td>
              <td className="text-center">0</td>
            </tr>
          </tbody>
        </table>

        <p className="text-muted mb-0" style={{ fontSize: '0.8em' }}>
          {t('projects.offers.confirmRestoreSnapshotProceed')}
        </p>
      </div>
    );

    if (!await confirm(confirmMessage)) return;

    try {
      const result = await api.post(`projects/api/project-offers/${offer.id}/restore_snapshot/`);
      setViewingSnapshotOffer(null);
      queryClient.invalidateQueries({ queryKey: ['project-offers-all', projectId] });
      if (result?.warnings?.length) {
        toast.warn(t('projects.offers.restoreSnapshotWarnings', { warnings: result.warnings.join('; ') }));
      } else {
        toast.success(t('projects.offers.snapshotRestored'));
      }
    } catch (err) {
      console.error('Error restoring snapshot:', err);
      toast.error(t('projects.offers.errorRestoreSnapshot'));
    }
  };

  const toggleExpand = (offerId) =>
    setExpandedOfferId(prev => prev === offerId ? null : offerId);

  const handleOpenCreate = () => {
    if (projectData?.customer?.id) {
      const cd = projectData.customer;
      setSelectedCustomerOption({ value: cd.id, label: `${cd.company_name || ''} | ${cd.resp_person || ''}` });
    }

    const pb = projectData?.processed_by;
    if (pb?.id) {
      const contrOpt = { value: pb.id, label: `${pb.company?.company_name || ''} | ${pb.display_name || ''}` };
      setSelectedContractorOption(contrOpt);
      setContractorOptions(prev => prev.some(o => o.value === pb.id) ? prev : [contrOpt, ...prev]);
    } else {
      setSelectedContractorOption(null);
    }

    setFormErrors({});
    setFormData(prev => ({
      ...prev,
      id: undefined,
      name: t('projects.offers.defaultName'),
      customer: projectData?.customer?.id || '',
      contractor: pb?.id || '',
      date_issued: new Date().toISOString().split('T')[0],
      offer_valid_until: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
      version: '1.0',
      discount: projectDiscount,
      note: '',
      include_items: true,
      hide_prices: false,
      hide_unit_prices: true,
      include_customer_info: true,
      include_vat: true,
      show_category_totals: true,
      group_by_parent_category: true,
      hide_licence_plates: true,
      include_notes: true,
      hide_staff_info: true,
      template: 'default',
      template_content: '',
      description: ''
    }));
    setShowModal(true);
  };

  const renderOfferSummaryColumn = (title, emptyLabel, items, renderValue, color = VERSION_BADGE_COLOR) => (
    <div className="col-md-3">
      <div className="fw-semibold small text-muted text-uppercase mb-2">{title}</div>
      {items.length === 0 ? (
        <span className="text-muted small">{emptyLabel}</span>
      ) : (
        <div className="d-flex flex-column gap-2">
          {items.map((item, index) => (
            <div key={item.id || item.code || item.name || index} className="d-flex justify-content-between align-items-center gap-3 border rounded px-2 py-2 bg-body-tertiary">
              <span className="small text-truncate">{item.name}</span>
              <StatusBadge color={color} label={renderValue(item)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) return <CenteredLoader />;

  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <>
      <PageSection
        title={t('projects.offers.title')}
        description={t('projects.offers.description')}
        bodyClassName="p-0"
        actions={canManage ? (
          <div className="btn-group btn-group-sm">
            <Button variant="outline-secondary" size="sm" onClick={handleQuickPreview} disabled={previewLoading}>
              <i className="ti ti-eye me-1" aria-hidden="true"></i>
              {previewLoading ? t('projects.offers.generatingPreview') : t('projects.offers.previewPdf')}
            </Button>
            <Button variant="primary" size="sm" onClick={handleOpenCreate}>
              <i className="ti ti-plus me-1" aria-hidden="true"></i>
              {t('projects.offers.createOffer')}
            </Button>
          </div>
        ) : null}
      >
        <div className="border-bottom">
          <Button
            variant="link"
            className="w-100 d-flex align-items-center justify-content-between gap-3 px-3 py-3 text-start text-decoration-none text-reset rounded-0 border-0"
            onClick={() => setIsSnapshotSummaryOpen(prev => !prev)}
            aria-expanded={isSnapshotSummaryOpen}
            aria-controls="project-offers-summary"
          >
            <span className="d-flex align-items-center gap-2 text-muted small text-uppercase fw-semibold">
              <i className="ti ti-list-details" aria-hidden="true"></i>
              {t('projects.offers.snapshotSummaryTitle')}
            </span>
            <span className="d-flex align-items-center gap-2 small text-muted">
              {isSnapshotSummaryOpen ? t('projects.offers.hideSnapshotSummary') : t('projects.offers.showSnapshotSummary')}
              <i className={`ti ti-chevron-${isSnapshotSummaryOpen ? 'up' : 'down'}`} aria-hidden="true"></i>
            </span>
          </Button>
          <Collapse in={isSnapshotSummaryOpen}>
            <div id="project-offers-summary" className="px-3 pb-3">
              {viewingSnapshotOffer && (
                <div className="d-flex align-items-center justify-content-between mb-3 px-3 py-2 rounded border" style={{ backgroundColor: 'var(--tblr-info-lt, #e8f4fb)', borderColor: 'var(--tblr-info)' }}>
                  <span className="small fw-semibold" style={{ color: 'var(--tblr-info)' }}>
                    <i className="ti ti-camera me-2" aria-hidden="true"></i>
                    {t('projects.offers.viewingSnapshot')} — {viewingSnapshotOffer.name} v{viewingSnapshotOffer.version || '1.0'}
                  </span>
                  <div className="d-flex gap-2">
                    {canManage && (
                      <Button size="sm" variant="outline-danger" onClick={() => handleRestoreSnapshot(viewingSnapshotOffer)}>
                        <i className="ti ti-history me-1" aria-hidden="true"></i>
                        {t('projects.offers.restoreSnapshot')}
                      </Button>
                    )}
                    <Button size="sm" variant="outline-secondary" onClick={() => setViewingSnapshotOffer(null)}>
                      <i className="ti ti-x me-1" aria-hidden="true"></i>
                      {t('projects.offers.backToLiveData')}
                    </Button>
                  </div>
                </div>
              )}
              <div className="row g-3">
                {renderOfferSummaryColumn(
                  t('projects.offers.offerItems'),
                  t('projects.offers.noProjectItems'),
                  summaryItems,
                  item => item.quantity,
                  '#6c7a91'
                )}
                {renderOfferSummaryColumn(
                  t('projects.items.externalItems'),
                  t('projects.items.noExternalItems'),
                  summaryExternalItems,
                  item => item.quantity,
                  '#d97706'
                )}
                {renderOfferSummaryColumn(
                  t('projects.offers.offerStaff'),
                  t('projects.offers.noProjectStaff'),
                  summaryStaff,
                  item => item.role,
                  '#0d6efd'
                )}
                {renderOfferSummaryColumn(
                  t('projects.offers.offerVehicles'),
                  t('projects.offers.noProjectVehicles'),
                  summaryVehicles,
                  item => item.tariff,
                  '#0f766e'
                )}
              </div>
            </div>
          </Collapse>
        </div>

          {error ? <div className="px-3 pt-3"><Alert variant="danger" className="app-inline-alert">{error}</Alert></div> : null}

          {offers.length === 0 ? (
            <div className="px-3 py-3">
              <EmptyState
                title={t('projects.offers.noOffers')}
                message={t('projects.offers.emptyMessage')}
                action={canManage ? (
                  <Button variant="primary" onClick={handleOpenCreate}>
                    <i className="ti ti-plus me-2" aria-hidden="true"></i>
                    {t('projects.offers.createOffer')}
                  </Button>
                ) : null}
              />
            </div>
          ) : (
            <Table responsive hover className="app-table mb-0">
              <thead>
                <tr>
                  <th>{t('offers.name')}</th>
                  <th>{t('projects.offers.version')}</th>
                  <th>{t('offers.customer')}</th>
                  <th>{t('offers.contractor')}</th>
                  <th>{t('offers.dateIssued')}</th>
                  <th>{t('offers.validUntil')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {offers.map(offer => {
                    const companyName = offer.customer_detail?.company_name || '';
                    const isCustomerCompanySliced = companyName.length > 10;
                    const isExpanded = expandedOfferId === offer.id;
                    return (
                      <React.Fragment key={offer.id}>
                        <tr>
                            <td>{offer.name}</td>
                            <td>
                              <StatusBadge color={VERSION_BADGE_COLOR} label={offer.version || '1.0'} />
                            </td>
                            <td>{companyName.slice(0, 10)}{isCustomerCompanySliced ? '...' : ''}{offer.customer_detail?.resp_person ? ` | ${offer.customer_detail.resp_person.trim()}` : ''}</td>
                            <td>{offer.contractor_detail?.display_name || t('common.notSet')}</td>
                            <td>{formatDate(offer.date_issued)}</td>
                            <td>{formatDate(offer.offer_valid_until)}</td>
                            <td>
                                <div className="btn-group btn-group-sm">
                                  <Button
                                    variant={isExpanded ? 'secondary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => toggleExpand(offer.id)}
                                    title={isExpanded ? t('projects.offers.hideDetails') : t('projects.offers.showDetails')}
                                    aria-label={isExpanded ? t('projects.offers.hideDetails') : t('projects.offers.showDetails')}
                                  >
                                    <i className={`ti ti-chevron-${isExpanded ? 'up' : 'down'}`} aria-hidden="true"></i>
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => handleShow(offer.id)}
                                    title={t('offers.viewPdf')}
                                    aria-label={t('offers.viewPdf')}
                                  >
                                    <i className="ti ti-eye" aria-hidden="true"></i>
                                  </Button>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleDownload(offer.id)}
                                    title={t('offers.download')}
                                    aria-label={t('offers.download')}
                                  >
                                    <i className="ti ti-download" aria-hidden="true"></i>
                                  </Button>
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() => handleExportExcel(offer.id)}
                                    title={t('projects.offers.exportExcel')}
                                    aria-label={t('projects.offers.exportExcel')}
                                  >
                                    <i className="ti ti-file-spreadsheet" aria-hidden="true"></i>
                                  </Button>
                                  {canManage && (
                                    <>
                                      <Button
                                        variant="outline-info"
                                        size="sm"
                                        onClick={() => handleEdit(offer)}
                                        title={t('common.edit')}
                                        aria-label={t('common.edit')}
                                      >
                                        <i className="ti ti-edit" aria-hidden="true"></i>
                                      </Button>
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleRefreshSnapshot(offer.id)}
                                        title={t('projects.offers.refreshSnapshot')}
                                        aria-label={t('projects.offers.refreshSnapshot')}
                                      >
                                        <i className="ti ti-refresh" aria-hidden="true"></i>
                                      </Button>
                                      {offer.snapshot && (
                                        <Button
                                          variant="outline-danger"
                                          size="sm"
                                          onClick={() => handleRestoreSnapshot(offer)}
                                          title={t('projects.offers.restoreSnapshot')}
                                          aria-label={t('projects.offers.restoreSnapshot')}
                                        >
                                          <i className="ti ti-history" aria-hidden="true"></i>
                                        </Button>
                                      )}
                                      <Button
                                        variant="outline-warning"
                                        size="sm"
                                        onClick={() => handleDuplicate(offer)}
                                        title={t('projects.offers.duplicateOffer')}
                                        aria-label={t('projects.offers.duplicateOffer')}
                                      >
                                        <i className="ti ti-copy" aria-hidden="true"></i>
                                      </Button>
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleDelete(offer.id)}
                                        title={t('common.delete')}
                                        aria-label={t('common.delete')}
                                      >
                                        <i className="ti ti-trash" aria-hidden="true"></i>
                                      </Button>
                                    </>
                                  )}
                                </div>
                            </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} className="p-0">
                              <div className="p-3 border-bottom" style={{ backgroundColor: 'var(--tblr-bg-surface-secondary, var(--tblr-bg-surface))' }}>
                                {!offer.snapshot && (
                                  <div className="alert alert-warning py-2 mb-3 d-flex align-items-center gap-2" style={{ fontSize: '0.8rem' }}>
                                    <i className="ti ti-alert-triangle"></i>
                                    {t('projects.offers.noSnapshot')}
                                  </div>
                                )}
                                <div className="row g-4">
                                  <div className="col-md-3">
                                    <div className="fw-semibold mb-2 text-muted text-uppercase small">
                                      {t('projects.offers.offerItems')}
                                      {!offer.include_items && (
                                        <span className="ms-2 align-middle">
                                          <StatusBadge color="#d97706" label={t('projects.offers.itemsNotIncluded')} />
                                        </span>
                                      )}
                                    </div>
                                    {(() => {
                                      const snapItems = offer.snapshot?.items ?? null;
                                      const displayItems = snapItems !== null
                                        ? snapItems
                                        : projectItems.map(pi => ({ name: pi.item_name, quantity: pi.quantity }));
                                      return displayItems.length === 0 ? (
                                        <span className="text-muted small">{t('projects.offers.noProjectItems')}</span>
                                      ) : (
                                        <ul className="list-unstyled mb-0">
                                          {displayItems.map((item, idx) => (
                                            <li key={idx} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                              <span className="small">{item.name}</span>
                                              <span className="badge bg-secondary-lt text-secondary ms-2">×{item.quantity}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      );
                                    })()}
                                  </div>
                                  <div className="col-md-3">
                                    <div className="fw-semibold mb-2 text-muted text-uppercase small">
                                      {t('projects.items.externalItems')}
                                    </div>
                                    {(() => {
                                      // If a snapshot exists, use its external_items ([] for old snapshots that predate this feature).
                                      // Only fall back to live data when there is no snapshot at all.
                                      const displayExt = offer.snapshot
                                        ? (offer.snapshot.external_items ?? [])
                                        : projectExternalItems.map(e => ({ name: e.name, quantity: e.quantity }));
                                      return displayExt.length === 0 ? (
                                        <span className="text-muted small">{t('projects.items.noExternalItems')}</span>
                                      ) : (
                                        <ul className="list-unstyled mb-0">
                                          {displayExt.map((ext, idx) => (
                                            <li key={idx} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                              <span className="small fst-italic text-muted">{ext.name}</span>
                                              <span className="badge bg-orange-lt text-orange ms-2">×{ext.quantity}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      );
                                    })()}
                                  </div>
                                  <div className="col-md-3">
                                    <div className="fw-semibold mb-2 text-muted text-uppercase small">
                                      {t('projects.offers.offerStaff')}
                                    </div>
                                    {(() => {
                                      const snapStaff = offer.snapshot?.staff ?? null;
                                      const displayStaff = snapStaff !== null
                                        ? snapStaff
                                        : projectStaff.map(ps => ({ name: ps.crew_member_detail?.display_name || ps.display_name, role: ps.role }));
                                      return displayStaff.length === 0 ? (
                                        <span className="text-muted small">{t('projects.offers.noProjectStaff')}</span>
                                      ) : (
                                        <ul className="list-unstyled mb-0">
                                          {displayStaff.map((member, idx) => (
                                            <li key={idx} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                              <span className="small">{member.name}</span>
                                              <span className="badge bg-azure-lt text-azure ms-2">{member.role}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      );
                                    })()}
                                  </div>
                                  <div className="col-md-3">
                                    <div className="fw-semibold mb-2 text-muted text-uppercase small">
                                      {t('projects.offers.offerVehicles')}
                                    </div>
                                    {(() => {
                                      const snapVehicles = offer.snapshot?.vehicles ?? null;
                                      const displayVehicles = snapVehicles !== null
                                        ? snapVehicles
                                        : projectVehicles.map(pv => ({ name: pv.vehicle_name, km: pv.km, rate_type: pv.rate_type }));
                                      return displayVehicles.length === 0 ? (
                                        <span className="text-muted small">{t('projects.offers.noProjectVehicles')}</span>
                                      ) : (
                                        <ul className="list-unstyled mb-0">
                                          {displayVehicles.map((vehicle, idx) => (
                                            <li key={idx} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                              <span className="small">{vehicle.name}</span>
                                              <span className="badge bg-teal-lt text-teal ms-2">{vehicle.rate_type === 'km' ? `${vehicle.km} km` : `${vehicle.km} dní`}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      );
                                    })()}
                                  </div>
                                </div>
                                {offer.note && (
                                  <div className="mt-3 pt-3 border-top">
                                    <div className="fw-semibold mb-1 text-muted text-uppercase small">{t('offers.notes')}</div>
                                    <p className="small mb-0" style={{ whiteSpace: 'pre-wrap' }}>{offer.note}</p>
                                  </div>
                                )}
                                {offer.include_notes && offer.snapshot?.project_notes?.length > 0 && (
                                  <div className="mt-3 pt-3 border-top">
                                    <div className="fw-semibold mb-1 text-muted text-uppercase small">{t('projects.notes.title', 'Project Notes')}</div>
                                    {offer.snapshot.project_notes.map((note, idx) => (
                                      <p key={idx} className="small mb-1" style={{ whiteSpace: 'pre-wrap', borderLeft: '3px solid var(--tblr-border-color)', paddingLeft: '8px' }}>{note.content}</p>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                })}
              </tbody>
            </Table>
          )}
      </PageSection>

      {/* Create Offer Modal */}
      <Modal show={showModal} onHide={() => { setShowModal(false); setSelectedContractorOption(null); setFormErrors({}); }} size="lg" fullscreen="sm-down">
        <Modal.Header closeButton>
          <Modal.Title>
            {formData.id ? t('projects.offers.editOffer') : t('projects.offers.createOffer')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpdate}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('offers.name')}</Form.Label>
                  <CreatableSelect
                    value={formData.name ? { value: formData.name, label: formData.name } : null}
                    onChange={(option) => handleSelectChange('name', option)}
                    onCreateOption={(inputValue) => handleSelectChange('name', { value: inputValue, label: inputValue })}
                    options={offerNameOptions}
                    isClearable
                    isSearchable
                    theme={selectTheme}
                    className={`react-select-container${formErrors.name ? ' is-invalid' : ''}`}
                    classNamePrefix="react-select"
                    placeholder={t('offers.name')}
                    noOptionsMessage={() => t('projects.offers.noPreviousNames')}
                    formatCreateLabel={(input) => `${t('common.create')}: "${input}"`}
                  />
                  {formErrors.name && <div className="invalid-feedback d-block">{formErrors.name}</div>}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('offers.template')}</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Select
                      name="template"
                      value={formData.template}
                      onChange={handleChange}
                      style={{ flex: 1 }}
                    >
                      <option value="default">{t('projects.offers.defaultTemplate')}</option>
                      <option value="detailed">{t('projects.offers.detailedTemplate')}</option>
                      <option value="simple">{t('projects.offers.simpleTemplate')}</option>
                      <option value="custom">{t('projects.offers.customTemplate')}</option>
                    </Form.Select>
                    {formData.template === 'custom' && (
                      <Button
                        variant="outline-primary"
                        onClick={() => setShowTemplateEditor(true)}
                        title={t('projects.offers.templates.editTemplate')}
                      >
                        <i className="ti ti-code"></i>
                      </Button>
                    )}
                  </div>
                  {formData.template === 'custom' && !formData.template_content && (
                    <Form.Text className="text-warning">
                      {t('projects.offers.templates.noCustomTemplate')}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('offers.customer')}</Form.Label>
                  <Select
                    value={selectedCustomerOption}
                    onChange={(option) => handleSelectChange('customer', option)}
                    options={customerOptions}
                    onInputChange={(inputValue) => debouncedCustomerSearch(inputValue)}
                    placeholder={t('projects.offers.searchCustomer')}
                    isClearable
                    isSearchable
                    theme={selectTheme}
                    className={`react-select-container${formErrors.customer ? ' is-invalid' : ''}`}
                    classNamePrefix="react-select"
                  />
                  {formErrors.customer && <div className="invalid-feedback d-block">{formErrors.customer}</div>}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('offers.contractor')}</Form.Label>
                  <Select
                    value={selectedContractorOption}
                    onChange={(option) => handleSelectChange('contractor', option)}
                    options={contractorOptions}
                    onInputChange={(inputValue) => debouncedContractorSearch(inputValue)}
                    placeholder={t('projects.offers.searchContractor')}
                    isClearable
                    isSearchable
                    theme={selectTheme}
                    className={`react-select-container${formErrors.contractor ? ' is-invalid' : ''}`}
                    classNamePrefix="react-select"
                  />
                  {formErrors.contractor && <div className="invalid-feedback d-block">{formErrors.contractor}</div>}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('offers.dateIssued')}</Form.Label>
                  <Form.Control
                    type="date"
                    name="date_issued"
                    value={formData.date_issued}
                    onChange={handleChange}
                    isInvalid={!!formErrors.date_issued}
                  />
                  <Form.Control.Feedback type="invalid">{formErrors.date_issued}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('offers.validUntil')}</Form.Label>
                  <Form.Control
                    type="date"
                    name="offer_valid_until"
                    value={formData.offer_valid_until}
                    onChange={handleChange}
                    isInvalid={!!formErrors.offer_valid_until}
                  />
                  <Form.Control.Feedback type="invalid">{formErrors.offer_valid_until}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('offers.version')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="version"
                    value={formData.version}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('offers.discount')} (%)</Form.Label>
                  <Form.Control
                    type="number"
                    name="discount"
                    value={formData.discount}
                    onChange={handleChange}
                    min="0"
                    max="100"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>{t('offers.notes')}</Form.Label>
              <Form.Control
                as="textarea"
                name="note"
                value={formData.note}
                onChange={handleChange}
                rows={3}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  id="include_items"
                  name="include_items"
                  label={t('offers.includeItems')}
                  checked={formData.include_items}
                  onChange={handleChange}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  id="hide_prices"
                  name="hide_prices"
                  label={t('offers.showPrices')}
                  checked={!formData.hide_prices}
                  onChange={e => setFormData(prev => ({ ...prev, hide_prices: !e.target.checked }))}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  id="show_category_totals"
                  name="show_category_totals"
                  label={t('offers.showCategoryTotals')}
                  checked={formData.show_category_totals}
                  onChange={handleChange}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  id="group_by_parent_category"
                  name="group_by_parent_category"
                  label={t('offers.groupByParentCategory')}
                  checked={formData.group_by_parent_category}
                  onChange={handleChange}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  id="hide_licence_plates"
                  name="hide_licence_plates"
                  label={t('offers.hideLicencePlates')}
                  checked={formData.hide_licence_plates}
                  onChange={handleChange}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  id="include_notes"
                  name="include_notes"
                  label={t('offers.includeNotes')}
                  checked={formData.include_notes}
                  onChange={handleChange}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  id="hide_staff_info"
                  name="hide_staff_info"
                  label={t('offers.hideStaffInfo')}
                  checked={formData.hide_staff_info}
                  onChange={handleChange}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  id="hide_unit_prices"
                  name="hide_unit_prices"
                  label={t('offers.hideUnitPrices')}
                  checked={formData.hide_unit_prices}
                  onChange={handleChange}
                  className="mb-2"
                />
              </Col>
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  id="include_customer_info"
                  name="include_customer_info"
                  label={t('offers.includeCustomerInfo')}
                  checked={formData.include_customer_info}
                  onChange={handleChange}
                  className="mb-2"
                />
                <Form.Check
                  type="checkbox"
                  id="include_vat"
                  name="include_vat"
                  label={t('offers.includeVat')}
                  checked={formData.include_vat}
                  onChange={handleChange}
                  className="mb-2"
                />
              </Col>
            </Row>

            <div className="d-flex justify-content-end">
              <Button variant="outline-secondary" className="me-auto" onClick={handlePreview} disabled={isSavingOffer}>
                <i className="ti ti-eye me-1"></i>
                {t('projects.offers.previewPdf')}
              </Button>
              <Button variant="secondary" className="me-2" onClick={() => { setShowModal(false); setSelectedContractorOption(null); setFormErrors({}); }} disabled={isSavingOffer}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" type="submit" disabled={isSavingOffer}>
                {isSavingOffer && <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>}
                {formData.id ? t('common.update') : t('common.create')}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Template Editor Modal */}
      <TemplateEditor
        show={showTemplateEditor}
        onHide={() => setShowTemplateEditor(false)}
        onSave={(templateContent) => {

          // Use functional setState to ensure we get the latest state
          setFormData(prevFormData => {
            const updatedFormData = {
              ...prevFormData,
              template_content: templateContent
            };


            // Verify the update immediately
            setTimeout(() => {
                                                      }, 100);

            return updatedFormData;
          });
        }}
        initialTemplate={formData.template_content}
        templateType={formData.template}
        offerData={formData}
        projectData={projectData}
        projectItems={projectItems}
        customerData={formData.customer ? customers.find(c => c.id === parseInt(formData.customer)) : null}
        contractorData={formData.contractor ? contractors.find(c => c.id === parseInt(formData.contractor)) : null}
      />
    </>
  );
};

export default ProjectOffers;
